/**
 * 65.32 (DECISIONS 189): loadouts from squad layouts. A side's layout (how many of each squad type) sets its size and its
 * squads, and each member gets its slot: primary, secondary, grenades when the slot sets them. A side with no layout is dealt
 * as before (the gun shares, the side's rocket switch). Pure.
 */
import { armourIssueOf, armourChoiceOf } from './armour';
import type { GameConfig, GroundWeaponKind, LongGunKind, SecondaryKind, GameState, MemberLoadout, PlayerState, SideLayout, SquadType } from '../types';
import { SQUAD_TYPES, LAYOUT_MAX_PEOPLE, GROUND_WEAPONS } from '../constants';
import { LONG_GUNS } from '../types';
import { carriesRocket } from './rocketStock';
import { primaryOf, sidearmOf } from './weapons';

/** "line:2,support:1" → a layout (unknown types and non-positive counts dropped); null for nothing readable. */
export function parseLayout(text: string | undefined | null, types: Record<string, SquadType> = SQUAD_TYPES): SideLayout | null {
  if (!text) return null;
  const out: SideLayout = [];
  for (const part of text.split(',').map(x => x.trim()).filter(Boolean)) {
    const [k, v] = part.split(':').map(x => x.trim());
    const n = v === undefined ? 1 : Math.floor(Number(v));
    if (types[k] && Number.isFinite(n) && n > 0) out.push({ type: k, count: n });
  }
  return out.length ? out : null;
}

/** The layout's squads, each its members' slots, in order — whole squads up to `max` people (the Lab's cap). */
export function layoutSquads(layout: SideLayout, types: Record<string, SquadType> = SQUAD_TYPES, max = LAYOUT_MAX_PEOPLE): { squads: MemberLoadout[][]; people: number; clamped: boolean } {
  const squads: MemberLoadout[][] = [];
  let people = 0, clamped = false;
  for (const { type, count } of layout) {
    const t = types[type];
    if (!t || !t.members.length) continue;
    for (let i = 0; i < count; i++) {
      if (people + t.members.length > max) { clamped = true; continue; }
      squads.push(t.members.map(m => ({ ...m })));
      people += t.members.length;
    }
  }
  return { squads, people, clamped };
}

/** A layout's people (whole squads, capped as `layoutSquads`). */
export function layoutPeople(layout: SideLayout, types: Record<string, SquadType> = SQUAD_TYPES, max = LAYOUT_MAX_PEOPLE): number {
  return layoutSquads(layout, types, max).people;
}

/** The squads as index groups within the side (members in id order: consecutive). */
export function layoutGroups(layout: SideLayout, types: Record<string, SquadType> = SQUAD_TYPES, max = LAYOUT_MAX_PEOPLE): number[][] {
  let at = 0;
  return layoutSquads(layout, types, max).squads.map(sq => sq.map(() => at++));
}

/** Each unit's slot on a side with a layout (members in id order, squad by squad). */
export function layoutSlots(state: Pick<GameState, 'players'>, layoutByTeam: ReadonlyArray<SideLayout | undefined>, types: Record<string, SquadType> = SQUAD_TYPES): Map<number, MemberLoadout> {
  const slot = new Map<number, MemberLoadout>();
  layoutByTeam.forEach((layout, team) => {
    if (!layout) return;
    const ids = state.players.filter(p => p.teamIndex === team).map(p => p.id).sort((a, b) => a - b);
    layoutSquads(layout, types).squads.flat().forEach((m, i) => { if (ids[i] !== undefined) slot.set(ids[i], m); });
  });
  return slot;
}

/** Does any member of the layout carry the rocket tube? */
export function layoutHasRocket(layout: SideLayout, types: Record<string, SquadType> = SQUAD_TYPES): boolean {
  return layoutSquads(layout, types).squads.some(sq => sq.some(m => m.secondary === 'rocket'));
}

/**
 * Each member of a side with a layout gets its slot (members in id order, squad by squad): its primary (the LMG's carry on the
 * walk, as dealLongGuns) and its secondary; the setup's grenade issue reads the slot's grenades (layoutSlots). Sides with no layout are left as they are.
 */
export function issueLayouts(state: GameState, layoutByTeam: ReadonlyArray<SideLayout | undefined>, types: Record<string, SquadType> = SQUAD_TYPES): GameState {
  const slot = layoutSlots(state, layoutByTeam, types);
  if (!slot.size) return state;
  return { ...state, players: state.players.map(p => {
    const m = slot.get(p.id);
    if (!m) return p;
    const carry = GROUND_WEAPONS[m.primary].carry;
    const { secondary: _s, primary: _p, sidearm: _a, role: _r, ...rest } = p;
    let modifiers = p.modifiers;
    if (modifiers) { const { walkSpeedMult: _w, ...mods } = modifiers; modifiers = carry !== undefined ? { ...mods, walkSpeedMult: carry } : mods; }
    return {
      ...rest,
      ...(m.primary === 'rifle' ? {} : { primary: m.primary }),
      ...(m.secondary ? { secondary: m.secondary } : {}),
      ...(m.sidearm ? { sidearm: m.sidearm } : {}),   // 65.43: the M1911 in the pistol's place
      ...(m.role ? { role: m.role } : {}),   // 201 (8): the medic or the engineer (drawn); 69.45: the support (its case with GameConfig.supportRole)
      ...(modifiers ? { modifiers } : {}),
    } as PlayerState;
  }) };
}

/** 65.32: the weapons a player's own loadout may name (65.31: the 40 mm). */
const CARRY_KINDS: readonly GroundWeaponKind[] = ['rifle', 'smg', 'lmg', 'pistol', 'rocket', 'gl40', 'garand', 'springfield', 'thompson', 'bar', 'm1911', 'panzerfaust', 'missile'];   // (65.49: the anti-dropship missile; 65.43: the classic guns)
/** At most this many entries in a player's own loadout. */
export const CARRY_MAX = 6;

/** "lmg,rocket,rocket,pistol" (or with +) → the list (unknown names dropped, CARRY_MAX at most); null for nothing readable. */
export function parsePlayerLoadout(text: string | undefined | null): GroundWeaponKind[] | null {
  if (!text) return null;
  const out = text.split(/[,+]/).map(x => x.trim().toLowerCase()).filter((x): x is GroundWeaponKind => (CARRY_KINDS as readonly string[]).includes(x)).slice(0, CARRY_MAX);
  return out.length ? out : null;
}

/** The unit the player's own loadout goes to: its team's first by id (the unit the page hands the human). */
export function favouredUnitId(state: Pick<GameState, 'players'>, team: number): number | null {
  let best: number | null = null;
  for (const p of state.players) if (p.teamIndex === team && (best === null || p.id < best)) best = p.id;
  return best;
}

/**
 * The player's own loadout on its unit, outside the slots: `carry` (what the human's X steps through), the first long gun as
 * its primary (an LMG's carry on the walk), its first rocket or 40 mm as its secondary (so a bot on it, and the side's rules, read one),
 * and each rocket past the first a tube's issue more as spare rounds. Again at a respawn (the rockets).
 */
export function applyCarry(p: PlayerState, carry: readonly GroundWeaponKind[]): PlayerState {
  const gun = carry.find((k): k is LongGunKind => (LONG_GUNS as readonly string[]).includes(k));
  const rockets = carry.filter(k => k === 'rocket').length;
  const second = carry.find((k): k is SecondaryKind => k === 'rocket' || k === 'gl40' || k === 'panzerfaust' || k === 'missile');
  const sidearm = carry.includes('m1911') ? 'm1911' as const : undefined;   // 65.43: the M1911 named, in the pistol's place
  const spec = GROUND_WEAPONS.rocket, issue = spec.magazine + (spec.reserve ?? 0);
  const { primary: _p, secondary: _s, sidearm: _a, ...rest } = p;
  let modifiers = p.modifiers;
  if (modifiers && gun) { const { walkSpeedMult: _w, ...mods } = modifiers; const c = GROUND_WEAPONS[gun].carry; modifiers = c !== undefined ? { ...mods, walkSpeedMult: c } : mods; }
  return {
    ...rest,
    carry: [...carry],
    ...(gun && gun !== 'rifle' ? { primary: gun } : gun ? {} : p.primary ? { primary: p.primary } : {}),
    ...(second ? { secondary: second } : p.secondary ? { secondary: p.secondary } : {}),
    ...(sidearm ? { sidearm } : p.sidearm ? { sidearm: p.sidearm } : {}),
    ...(rockets > 1 ? { reserves: { ...(p.reserves ?? {}), rocket: rockets * issue - spec.magazine } } : {}),
    ...(modifiers ? { modifiers } : {}),
  } as PlayerState;
}

/** The order the human's X steps through: the player's own loadout (each kind once), else the primary, the pistol and the tube. */
export function weaponCycle(unit: Pick<PlayerState, 'carry' | 'primary' | 'teamIndex' | 'secondary'> & Partial<Pick<PlayerState, 'sidearm'>>, config: Pick<GameConfig, 'rockets' | 'rocketTeams' | 'layoutTeams'>): GroundWeaponKind[] {
  if (unit.carry?.length) return [...new Set(unit.carry)];
  return [primaryOf(unit), sidearmOf(unit), ...(carriesRocket(unit, config) ? ['rocket' as const] : []), ...(carriesGl40(unit) ? ['gl40' as const] : []), ...(carriesPanzerfaust(unit) ? ['panzerfaust' as const] : []), ...(carriesMissile(unit) ? ['missile' as const] : [])];
}

/** 65.31: does this unit carry the 40 mm launcher — the player's own loadout's list, else its slot's secondary. */
export function carriesGl40(unit: Pick<PlayerState, 'secondary'> & { carry?: PlayerState['carry'] }): boolean {
  return unit.carry ? unit.carry.includes('gl40') : unit.secondary === 'gl40';
}

/** 65.43: does this unit carry the Panzerfaust-style tube (in the LAW's place) — the player's own loadout's list, else its slot's secondary. */
export function carriesPanzerfaust(unit: Pick<PlayerState, 'secondary'> & { carry?: PlayerState['carry'] }): boolean {
  return unit.carry ? unit.carry.includes('panzerfaust') : unit.secondary === 'panzerfaust';
}

/** 65.49 step 2: does this unit carry the anti-dropship missile (in the LAW's place) — the player's own loadout's list, else its slot's secondary. */
export function carriesMissile(unit: Pick<PlayerState, 'secondary'> & { carry?: PlayerState['carry'] }): boolean {
  return unit.carry ? unit.carry.includes('missile') : unit.secondary === 'missile';
}

/**
 * 65.34: custom squad types as text — the Lab's builder writes it to `squadtypes=` and the page's URL path and labLaunch
 * read it, so a link and a headless run carry the same types. `key=m1/m2/…;key2=…`, each member `primary[+secondary][+m1911]
 * [+heXsY][+helmet|heavyhelmet|light|heavy|heavykit][+medic|engineer|support|crewman]` (e.g. `heavyAssault=lmg+heavy/rifle+rocket+light/smg+he2s1+light`;
 * 201 (8): `line=rifle/rifle+medic/rifle+engineer`). A key may be a
 * preset's: it overrides it. Unknown tokens are dropped; a type with no member, or a malformed key, is left out.
 */
export function parseSquadTypes(text: string | undefined | null): Record<string, SquadType> {
  const out: Record<string, SquadType> = {};
  if (!text) return out;
  for (const part of text.split(';').map(x => x.trim()).filter(Boolean)) {
    const [key, body] = part.split('=');
    if (!key || !/^[A-Za-z][A-Za-z0-9]{0,23}$/.test(key) || !body) continue;
    const members: MemberLoadout[] = [];
    for (const tok of body.split('/').slice(0, 8)) {
      const bits = tok.split('+').map(b => b.trim().toLowerCase());
      const primary = bits[0] as LongGunKind;
      if (!(LONG_GUNS as readonly string[]).includes(primary)) continue;
      const m: MemberLoadout = { primary };
      for (const b of bits.slice(1)) {
        if (b === 'rocket' || b === 'gl40' || b === 'panzerfaust' || b === 'missile') m.secondary = b;
        else if (b === 'm1911') m.sidearm = 'm1911';   // 65.43
        else if (b === 'medic' || b === 'engineer' || b === 'support' || b === 'crewman') m.role = b;   // 201 (8); 69.45: the support; 69.46: the crewman
        else if (/^he\d+s\d+$/.test(b)) { const [, he, sm] = b.match(/^he(\d+)s(\d+)$/)!; m.grenades = { he: Math.min(4, Number(he)), smoke: Math.min(4, Number(sm)) }; }
        else if (armourIssueOf(b)) m.armour = armourIssueOf(b);   // 190: helmet, heavyhelmet, light, heavy, heavykit
      }
      members.push(m);
    }
    if (members.length) out[key] = { key, name: key, members };
  }
  return out;
}

/** The text of custom squad types (parseSquadTypes' inverse). */
export function squadTypesText(types: Record<string, SquadType>): string {
  const tok = (m: MemberLoadout) => [m.primary, m.secondary ?? '', m.sidearm ?? '', m.grenades ? `he${m.grenades.he}s${m.grenades.smoke}` : '', (armourChoiceOf(m.armour) === 'none' ? '' : armourChoiceOf(m.armour).toLowerCase()), m.role ?? ''].filter(Boolean).join('+');
  return Object.values(types).map(t => `${t.key}=${t.members.map(tok).join('/')}`).join(';');
}

/** The squad types a layout counts: the presets, with the custom types over them (65.34). */
export function mergedSquadTypes(customText: string | undefined | null): Record<string, SquadType> {
  return { ...SQUAD_TYPES, ...parseSquadTypes(customText) };
}

/** A layout's text by its form only (`key:count,…`), whatever types exist — the Lab keeps a custom type's count until launch resolves it. */
export function layoutTextOk(text: string | undefined | null): boolean {
  return !!text && text.split(',').map(x => x.trim()).filter(Boolean).every(p => /^[A-Za-z][A-Za-z0-9]{0,23}(:\d{1,2})?$/.test(p)) && text.trim().length > 0;
}
